import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { tourService } from "@/services/tourService";

export const fetchTours = createAsyncThunk(
  "tour/fetchTours",
  async (
    {
      page = 1,
      pageSize = 3,
      search = "",
      priceMin = null,
      priceMax = null,
      categories = null,
      discounts = null,
      newest = null,
      bestsellers = null,
      reviews = null,
      sortBy = null,
    },
    thunkAPI
  ) => {
    try {
      const response = await tourService.getAllTours({
        page,
        pageSize,
        search,
        priceMin,
        priceMax,
        categories,
        discounts,
        newest,
        bestsellers,
        reviews,
        sortBy,
      });
      return response;
    } catch (error) {
      let errorMessage = "Lỗi không xác định";
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.title) {
          errorMessage = error.response.data.title;
          if (error.response.data.errors) {
            errorMessage +=
              ": " +
              Object.entries(error.response.data.errors)
                .map(([key, value]) => `${key}: ${value.join(", ")}`)
                .join("; ");
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const fetchTourById = createAsyncThunk(
  "tour/fetchTourById",
  async (tourId, thunkAPI) => {
    try {
      const response = await tourService.getTourById(tourId);
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data || error.message || "Lỗi không xác định";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const createTour = createAsyncThunk(
  "tour/createTour",
  async (tourData, thunkAPI) => {
    try {
      const response = await tourService.createTour(tourData);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data || error.message || "Lỗi không xác định";
      console.log(error.response?.data + error.message);
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const updateTour = createAsyncThunk(
  "tour/updateTour",
  async ({ tourId, tourData }, thunkAPI) => {
    try {
      const response = await tourService.updateTour(tourId, tourData);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data || error.message || "Lỗi không xác định";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const deleteTour = createAsyncThunk(
  "tour/deleteTour",
  async (tourId, thunkAPI) => {
    try {
      await tourService.deleteTour(tourId);
      return tourId;
    } catch (error) {
      const errorMessage =
        error.response?.data || error.message || "Lỗi không xác định";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const fetchCategoriesWithTourInfo = createAsyncThunk(
  "tour/fetchCategoriesWithTourInfo",
  async (_, thunkAPI) => {
    try {
      const response = await tourService.getCategoriesWithTourInfo();
      return response;
    } catch (error) {
      let errorMessage = "Lỗi không xác định";
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.title) {
          errorMessage = error.response.data.title;
          if (error.response.data.errors) {
            errorMessage +=
              ": " +
              Object.entries(error.response.data.errors)
                .map(([key, value]) => `${key}: ${value.join(", ")}`)
                .join("; ");
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Thunk để lấy 10 tour có đánh giá cao nhất
export const fetchTopRatedTours = createAsyncThunk(
  "tour/fetchTopRatedTours",
  async (_, thunkAPI) => {
    try {
      const response = await tourService.getTopRatedTours();
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data || error.message || "Lỗi không xác định";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Thunk để lấy 10 tour có nhiều booking nhất
export const fetchTopBookedTours = createAsyncThunk(
  "tour/fetchTopBookedTours",
  async (_, thunkAPI) => {
    try {
      const response = await tourService.getTopBookedTours();
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data || error.message || "Lỗi không xác định";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Thunk để lấy tour có discount cao nhất
export const fetchTopDiscountedTours = createAsyncThunk(
  "tour/fetchTopDiscountedTours",
  async (_, thunkAPI) => {
    try {
      const response = await tourService.getTopDiscountedTours();
      return response.data; // Lấy phần "data" từ response
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Lỗi không xác định";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const fetchPersonalizedToursByUser = createAsyncThunk(
  "tour/fetchPersonalizedToursByUser",
  async ({ userId, status = null }, thunkAPI) => {
    try {
      const response = await tourService.getPersonalizedToursByUser(
        userId,
        status
      );
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data || error.message || "Lỗi không xác định";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Tạo slice
const tourSlice = createSlice({
  name: "tour",
  initialState: {
    tours: [],
    totalPages: 1,
    currentPage: 1,
    searchQuery: "",
    selectedTour: null,
    loading: false,
    error: null,
    sortBy: null,
    totalCount: 0,
    categories: [],
    topRatedTours: [], // Thêm state cho top rated tours
    topBookedTours: [], // Thêm state cho top booked tours
    topDiscountedTours: [], // Thêm state cho top discounted tour
  },
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTours.fulfilled, (state, action) => {
        state.loading = false;
        state.tours = action.payload.data ?? [];
        state.totalCount = action.payload.totalCount ?? 0;
      })
      .addCase(fetchTours.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTourById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTourById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTour = action.payload ?? null;
      })
      .addCase(fetchTourById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTour.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTour.fulfilled, (state, action) => {
        state.loading = false;
        state.tours.push(action.payload);
      })
      .addCase(createTour.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update tour
      .addCase(updateTour.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTour.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.tours = state.tours.map((tour) =>
            tour.tourId === action.payload.tourId ? action.payload : tour
          );
        }
      })
      .addCase(updateTour.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete tour
      .addCase(deleteTour.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteTour.fulfilled, (state, action) => {
        state.loading = false;
        state.tours = state.tours.filter(
          (tour) => String(tour.tourId) !== String(action.payload)
        );
      })
      .addCase(deleteTour.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCategoriesWithTourInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoriesWithTourInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data ?? []; // Lưu data từ response vào state
      })
      .addCase(fetchCategoriesWithTourInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Xử lý fetchTopRatedTours
      .addCase(fetchTopRatedTours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopRatedTours.fulfilled, (state, action) => {
        state.loading = false;
        state.topRatedTours = action.payload.data ?? [];
      })
      .addCase(fetchTopRatedTours.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Xử lý fetchTopBookedTours
      .addCase(fetchTopBookedTours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopBookedTours.fulfilled, (state, action) => {
        state.loading = false;
        state.topBookedTours = action.payload.data ?? [];
      })
      .addCase(fetchTopBookedTours.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Xử lý fetchTopDiscountedTour
      .addCase(fetchTopDiscountedTours.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTopDiscountedTours.fulfilled, (state, action) => {
        state.loading = false;
        state.topDiscountedTours = action.payload; // Lưu danh sách
      })
      .addCase(fetchTopDiscountedTours.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPersonalizedToursByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPersonalizedToursByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.tours = action.payload.data;
      })
      .addCase(fetchPersonalizedToursByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSearchQuery, setSortBy } = tourSlice.actions;
export default tourSlice.reducer;
